// Utility functions for recurring tournament logic

// Day names array for converting between numbers and names
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Safely parse a time string in HH:MM format
 * Handles various formats and validates the result
 * @param {string} timeStr - Time string like "14:30" or "14:30:00"
 * @returns {{ hours: number, minutes: number } | null} - Parsed time or null if invalid
 */
const parseTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') {
    return null;
  }

  const parts = timeStr.split(':');
  if (parts.length < 2) {
    return null;
  }

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Validate hours and minutes are valid numbers in range
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
};

/**
 * Normalize dayOfWeek value to a day name string
 * Handles both legacy numeric values ("0", "1", etc.) and new day name strings ("Sunday", "Monday", etc.)
 * @param {string|number} dayOfWeek - The day of week value to normalize
 * @returns {string|null} - The day name or null if invalid
 */
const normalizeDayOfWeek = (dayOfWeek) => {
  if (dayOfWeek === undefined || dayOfWeek === null || dayOfWeek === '') {
    return null;
  }

  // If it's already a valid day name, return it
  if (DAY_NAMES.includes(dayOfWeek)) {
    return dayOfWeek;
  }

  // Try to parse as a number (handles "0", "1", etc. and 0, 1, etc.)
  const dayNumber = parseInt(dayOfWeek, 10);
  if (!isNaN(dayNumber) && dayNumber >= 0 && dayNumber <= 6) {
    return DAY_NAMES[dayNumber];
  }

  return null;
};

/**
 * Get the day of week as a number (0 = Sunday, 6 = Saturday)
 * Handles both day names and legacy numeric values
 */
const getDayNumber = (dayOfWeek) => {
  const normalizedDay = normalizeDayOfWeek(dayOfWeek);
  if (!normalizedDay) {
    return undefined;
  }
  return DAY_NAMES.indexOf(normalizedDay);
};

/**
 * Calculate the next occurrence date for a recurring tournament
 * @param {Object} recurring - Recurring tournament settings
 * @param {string} recurring.dayOfWeek - Day of week (e.g., "Monday")
 * @param {string} recurring.startTime - Start time in HH:MM format
 * @param {string} recurring.frequency - Frequency: "weekly", "bi-weekly", or "monthly"
 * @param {Date} fromDate - Optional: Calculate from this date (defaults to now)
 * @returns {Date|null} - Next occurrence date or null if invalid
 */
export const getNextOccurrence = (recurring, fromDate = new Date()) => {
  if (!recurring || !recurring.enabled || !recurring.dayOfWeek) {
    return null;
  }

  const targetDayNumber = getDayNumber(recurring.dayOfWeek);
  if (targetDayNumber === undefined) {
    return null;
  }

  const today = new Date(fromDate);
  today.setHours(0, 0, 0, 0);

  const currentDayNumber = today.getDay();

  // Calculate days until next occurrence
  let daysUntilNext = targetDayNumber - currentDayNumber;

  // If the target day is today, check if the time has passed
  if (daysUntilNext === 0 && recurring.startTime) {
    const parsedTime = parseTimeString(recurring.startTime);
    if (parsedTime) {
      const now = new Date(fromDate);
      const tournamentTime = new Date(today);
      tournamentTime.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);

      // If time has passed today, move to next week
      if (now >= tournamentTime) {
        daysUntilNext = 7;
      }
    }
  }

  // If target day is before current day, move to next week
  if (daysUntilNext < 0) {
    daysUntilNext += 7;
  }

  // If target day is today but we haven't set daysUntilNext to 7, keep it at 0
  // This handles the case where the tournament is today and hasn't started yet

  const nextDate = new Date(today);
  nextDate.setDate(today.getDate() + daysUntilNext);

  // Set the time if provided
  if (recurring.startTime) {
    const parsedTime = parseTimeString(recurring.startTime);
    if (parsedTime) {
      nextDate.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);
    }
  }

  return nextDate;
};

/**
 * Get the current or next occurrence date for a recurring tournament
 * This includes tournaments that have started but not yet completed
 * @param {Object} recurring - Recurring tournament settings
 * @returns {Date|null} - Current/next occurrence date or null if invalid
 */
export const getCurrentOrNextOccurrence = (recurring) => {
  if (!recurring || !recurring.enabled || !recurring.dayOfWeek) {
    return null;
  }

  const targetDayNumber = getDayNumber(recurring.dayOfWeek);
  if (targetDayNumber === undefined) {
    return null;
  }

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const currentDayNumber = today.getDay();

  // Calculate days until next occurrence
  let daysUntilNext = targetDayNumber - currentDayNumber;

  // If target day is before current day, move to next week
  if (daysUntilNext < 0) {
    daysUntilNext += 7;
  }

  // If the target day is today and has a start time, we need to check if it has started
  if (daysUntilNext === 0 && recurring.startTime) {
    const parsedTime = parseTimeString(recurring.startTime);
    if (parsedTime) {
      const tournamentTime = new Date(today);
      tournamentTime.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);

      // If the tournament time is in the future, return today's date
      // If the tournament time has passed, we still return today's date
      // (This is the key difference from getNextOccurrence - we include today's instance)
    }
  }

  const nextDate = new Date(today);
  nextDate.setDate(today.getDate() + daysUntilNext);

  // Set the time if provided
  if (recurring.startTime) {
    const parsedTime = parseTimeString(recurring.startTime);
    if (parsedTime) {
      nextDate.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);
    }
  }

  return nextDate;
};

/**
 * Get multiple upcoming occurrences
 * @param {Object} recurring - Recurring tournament settings
 * @param {number} count - Number of occurrences to return
 * @param {boolean} includeCurrentDay - If true, includes today's occurrence even if time has passed
 * @returns {Date[]} - Array of upcoming occurrence dates
 */
export const getUpcomingOccurrences = (recurring, count = 5, includeCurrentDay = false) => {
  if (!recurring || !recurring.enabled) {
    return [];
  }

  const occurrences = [];
  let currentDate = new Date();

  // If includeCurrentDay is true, first check if today is a valid occurrence
  // and include it even if the time has passed (for registration matching)
  if (includeCurrentDay) {
    const currentOccurrence = getCurrentOrNextOccurrence(recurring);
    if (currentOccurrence) {
      occurrences.push(new Date(currentOccurrence));
      // Move past this occurrence to find the next ones
      currentDate = new Date(currentOccurrence);
      currentDate.setDate(currentDate.getDate() + 1);
      count--; // We already have one occurrence
    }
  }

  for (let i = 0; i < count; i++) {
    const nextDate = getNextOccurrence(recurring, currentDate);
    if (!nextDate) break;

    // Avoid duplicates (in case getCurrentOrNextOccurrence and getNextOccurrence return the same date)
    const nextDateStr = nextDate.toISOString();
    if (!occurrences.some(d => d.toISOString() === nextDateStr)) {
      occurrences.push(new Date(nextDate));
    }

    // Move to 1 day after this occurrence to find the next one
    // This ensures we skip past the current occurrence
    currentDate = new Date(nextDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return occurrences;
};

/**
 * Format the next occurrence as a human-readable string
 * @param {Object} recurring - Recurring tournament settings
 * @returns {string} - Formatted string (e.g., "Next: Mon, Jan 15 at 7:00 PM")
 */
export const formatNextOccurrence = (recurring) => {
  const nextDate = getNextOccurrence(recurring);
  if (!nextDate) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const nextDateDay = new Date(nextDate);
  nextDateDay.setHours(0, 0, 0, 0);

  let dateString;

  // Check if it's today
  if (nextDateDay.getTime() === today.getTime()) {
    dateString = 'Today';
  }
  // Check if it's tomorrow
  else if (nextDateDay.getTime() === tomorrow.getTime()) {
    dateString = 'Tomorrow';
  }
  // Otherwise format the date
  else {
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    dateString = nextDate.toLocaleDateString('en-US', options);
  }

  // Format the time if available
  let timeString = '';
  if (recurring.startTime) {
    const parsedTime = parseTimeString(recurring.startTime);
    if (parsedTime) {
      const period = parsedTime.hours >= 12 ? 'PM' : 'AM';
      const displayHours = parsedTime.hours % 12 || 12;
      timeString = ` at ${displayHours}:${parsedTime.minutes.toString().padStart(2, '0')} ${period}`;
    }
  }

  return `${dateString}${timeString}`;
};

/**
 * Get a description of the recurring schedule
 * @param {Object} recurring - Recurring tournament settings
 * @returns {string} - Description (e.g., "Every Monday at 7:00 PM")
 */
export const getRecurringDescription = (recurring) => {
  if (!recurring || !recurring.enabled) {
    return '';
  }

  let frequencyText = '';
  switch (recurring.frequency) {
    case 'weekly':
      frequencyText = 'Every';
      break;
    case 'bi-weekly':
      frequencyText = 'Every other';
      break;
    case 'monthly':
      frequencyText = 'Monthly on';
      break;
    default:
      frequencyText = 'Every';
  }

  let timeText = '';
  if (recurring.startTime) {
    const parsedTime = parseTimeString(recurring.startTime);
    if (parsedTime) {
      const period = parsedTime.hours >= 12 ? 'PM' : 'AM';
      const displayHours = parsedTime.hours % 12 || 12;
      timeText = ` at ${displayHours}:${parsedTime.minutes.toString().padStart(2, '0')} ${period}`;
    }
  }

  // Normalize the day of week to handle legacy numeric values
  const dayName = normalizeDayOfWeek(recurring.dayOfWeek) || recurring.dayOfWeek;

  return `${frequencyText} ${dayName}${timeText}`;
};

/**
 * Check if a tournament is happening today
 * @param {Object} recurring - Recurring tournament settings
 * @returns {boolean}
 */
export const isHappeningToday = (recurring) => {
  if (!recurring || !recurring.enabled) {
    return false;
  }

  const today = new Date();
  const todayDayName = DAY_NAMES[today.getDay()];
  const normalizedDayOfWeek = normalizeDayOfWeek(recurring.dayOfWeek);

  return normalizedDayOfWeek === todayDayName;
};

/**
 * Check if a tournament is happening soon (within the next 7 days)
 * @param {Object} recurring - Recurring tournament settings
 * @returns {boolean}
 */
export const isHappeningSoon = (recurring) => {
  const nextDate = getNextOccurrence(recurring);
  if (!nextDate) return false;

  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

  return nextDate <= sevenDaysFromNow;
};

/**
 * Format a date as a local timezone ISO-like string for use as instance keys.
 * Unlike toISOString() which converts to UTC, this preserves the local timezone.
 * Format: "YYYY-MM-DDTHH:MM:SS.000" (no Z suffix, indicating local time)
 *
 * This is critical for recurring tournaments where we want the instance key
 * to reflect the local date/time the tournament occurs, not the UTC equivalent.
 *
 * @param {Date} date - The date to format
 * @returns {string} - Local timezone formatted date string
 */
export const formatLocalDateTime = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000`;
};

/**
 * Normalize an instance date key to ensure consistent format with .000 milliseconds.
 * This handles both "2026-03-10T19:30:00" and "2026-03-10T19:30:00.000" formats.
 *
 * @param {string} instanceDate - Instance date string in various formats
 * @returns {string|null} - Normalized instance date with .000 milliseconds, or null
 */
export const normalizeInstanceDate = (instanceDate) => {
  if (!instanceDate) return null;

  // Parse the date and re-format it to ensure consistent .000 milliseconds
  const date = new Date(instanceDate);
  if (isNaN(date.getTime())) return instanceDate; // Return as-is if invalid

  return formatLocalDateTime(date);
};

/**
 * Get instance data from a tournament's instances object, handling inconsistent key formats.
 * Tries both the exact key and the normalized key (with .000 milliseconds).
 *
 * @param {object} instances - The instances object from a tournament
 * @param {string} instanceDate - The instance date key to look up
 * @returns {{ data: object|null, key: string|null }} - The instance data and the matched key
 */
export const getInstanceData = (instances, instanceDate) => {
  if (!instances || !instanceDate) {
    return { data: null, key: null };
  }

  // Try exact key first
  if (instances[instanceDate]) {
    return { data: instances[instanceDate], key: instanceDate };
  }

  // Try normalized key (with .000 milliseconds)
  const normalizedKey = normalizeInstanceDate(instanceDate);
  if (normalizedKey && normalizedKey !== instanceDate && instances[normalizedKey]) {
    return { data: instances[normalizedKey], key: normalizedKey };
  }

  // Try key without milliseconds (for older data)
  const keyWithoutMs = instanceDate.replace(/\.000$/, '');
  if (keyWithoutMs !== instanceDate && instances[keyWithoutMs]) {
    return { data: instances[keyWithoutMs], key: keyWithoutMs };
  }

  return { data: null, key: null };
};

/**
 * Grace window (hours) a tournament stays "active" (still findable / upcoming)
 * after its scheduled start, when it has NOT been explicitly marked completed.
 * This covers a long single-session tournament and is the automatic backstop
 * that retires an unfinished tournament to history so it never shows as
 * perpetually "in progress".
 */
export const ACTIVE_GRACE_HOURS = 12;

/**
 * Instance lifecycle states shared by the Tournament Finder and the Instance
 * Navigator so both screens agree on when a tournament is findable vs. history.
 */
export const INSTANCE_STATUS = {
  UPCOMING: 'upcoming', // Scheduled, hasn't started yet
  ACTIVE: 'active',     // Started, within the grace window, still in progress
  COMPLETED: 'completed' // Marked complete OR past the grace window → history
};

/**
 * Determine the lifecycle status of a single tournament occurrence.
 *
 * Two signals drive the result:
 *   1. An explicit `completed` flag on the instance (fast path — set when the
 *      director finalizes the bracket) immediately retires it to history.
 *   2. A time-based grace window (`ACTIVE_GRACE_HOURS`, or an explicit end time)
 *      is the safety net: once elapsed, the occurrence retires to history even
 *      if the director never marked it complete.
 *
 * Callers treat UPCOMING and ACTIVE as "findable"; COMPLETED belongs to history.
 *
 * @param {Object} params
 * @param {Date|string|number} params.startTime - Scheduled start of the occurrence
 * @param {boolean} [params.completed] - Whether the instance was marked complete
 * @param {Date|string|number} [params.endTime] - Explicit end (for multi-day events)
 * @param {number} [params.graceHours] - Override for the default grace window
 * @param {Date} [params.now] - Reference "now" (injectable for testing)
 * @returns {'upcoming'|'active'|'completed'}
 */
export const getInstanceStatus = ({
  startTime,
  completed = false,
  endTime = null,
  graceHours = ACTIVE_GRACE_HOURS,
  now = new Date()
} = {}) => {
  if (completed) {
    return INSTANCE_STATUS.COMPLETED;
  }

  const start = startTime ? new Date(startTime) : null;
  // Without a valid start we can't reason about time; treat as upcoming so the
  // occurrence stays visible rather than silently disappearing.
  if (!start || isNaN(start.getTime())) {
    return INSTANCE_STATUS.UPCOMING;
  }

  if (now < start) {
    return INSTANCE_STATUS.UPCOMING;
  }

  // Determine when the occurrence should auto-retire to history: an explicit
  // end time if provided (multi-day events), otherwise start + grace window.
  const parsedEnd = endTime ? new Date(endTime) : null;
  const retireAt = (parsedEnd && !isNaN(parsedEnd.getTime()))
    ? parsedEnd
    : new Date(start.getTime() + graceHours * 60 * 60 * 1000);

  return now < retireAt ? INSTANCE_STATUS.ACTIVE : INSTANCE_STATUS.COMPLETED;
};

/**
 * Convenience predicate: is this occurrence still findable (upcoming or active)?
 * @param {Object} params - Same shape as getInstanceStatus
 * @returns {boolean}
 */
export const isInstanceFindable = (params) =>
  getInstanceStatus(params) !== INSTANCE_STATUS.COMPLETED;
